import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const CHECKLIST = [
  ["terms_reviewed", "Commercial terms reviewed against the deal"],
  ["clauses_reviewed", "Legal clauses reviewed / acceptable"],
  ["parties_verified", "Parties & authorized representatives verified"],
  ["compliance_cleared", "Compliance / sanctions considerations cleared (optional)"],
];

// Controlled legal/compliance sign-off dialog. Records an audited sign-off bound to the
// exact contract version; does NOT sign or execute anything.
export default function LegalSignoffDialog({ contract, open, onOpenChange, onDone }) {
  const [authority, setAuthority] = useState("");
  const [evidence, setEvidence] = useState("");
  const [notes, setNotes] = useState("");
  const [attest, setAttest] = useState(false);
  const [checks, setChecks] = useState({ terms_reviewed: false, clauses_reviewed: false, parties_verified: false, compliance_cleared: false });
  const [busy, setBusy] = useState(false);

  const requiredOk = checks.terms_reviewed && checks.clauses_reviewed && checks.parties_verified;

  const submit = async () => {
    if (!authority.trim()) return toast.error("Enter your legal authority / title");
    if (!attest) return toast.error("You must attest to the review");
    if (!requiredOk) return toast.error("Affirm all required checklist items");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.contract_id}/legal-signoff`, {
        authority_title: authority.trim(), attestation: true, evidence, notes,
        expected_version: contract.version,
        checklist: CHECKLIST.map(([item]) => ({ item, checked: !!checks[item] })),
      });
      toast.success("Legal sign-off recorded — contract is now LEGAL REVIEWED");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign-off failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="legal-signoff-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> Legal / Compliance Sign-off</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">
            Records an audited sign-off on <strong>{contract.title} v{contract.version}</strong>. This does not sign or execute the contract — it only authorizes moving it to “Approved for signature”.
          </p>
          <div>
            <Label htmlFor="lso-authority">Your legal authority / title</Label>
            <Input id="lso-authority" data-testid="lso-authority" value={authority} onChange={(e) => setAuthority(e.target.value)} placeholder="e.g. General Counsel" />
          </div>
          <div className="space-y-1.5">
            <Label>Review checklist</Label>
            {CHECKLIST.map(([item, label]) => (
              <label key={item} className="flex items-start gap-2 text-sm">
                <Checkbox data-testid={`lso-check-${item}`} checked={checks[item]} onCheckedChange={(v) => setChecks((c) => ({ ...c, [item]: !!v }))} />
                <span className="text-slate-700">{label}</span>
              </label>
            ))}
          </div>
          <div>
            <Label htmlFor="lso-evidence">Approval evidence / memo reference</Label>
            <Input id="lso-evidence" data-testid="lso-evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="e.g. Legal memo #, ticket, doc reference" />
          </div>
          <div>
            <Label htmlFor="lso-notes">Notes (optional)</Label>
            <Textarea id="lso-notes" data-testid="lso-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox data-testid="lso-attest" checked={attest} onCheckedChange={(v) => setAttest(!!v)} />
            <span className="text-slate-700">I attest that I have the authority to record legal/compliance sign-off for this version.</span>
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} disabled={busy} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
          <button data-testid="lso-submit" onClick={submit} disabled={busy || !requiredOk || !attest || !authority.trim()}
            className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Record sign-off
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
